import React from "react";
import { Tooltip } from "@material-ui/core";
import Card from "./Card";
import {
  COLORS,
  FONT,
  RADIUS,
  SERIES,
  cardStyle,
  compactNumber,
  decimal,
} from "./theme";

/**
 * Faixa de indicadores.
 *
 * Hierarquia proposital: UM número herói (o total do período, com a variação
 * contra a janela anterior), quatro tiles de apoio e, embaixo, as barras de
 * cobertura. Antes eram catorze cards do mesmo tamanho — o olho não tinha por
 * onde começar, que é o jeito mais fácil de um painel não ser lido.
 */

// Figura herói e valores de tile usam algarismos proporcionais (o padrão da
// fonte). `tabular-nums` só nas colunas de tabela e nos eixos: em tamanho
// grande ele deixa "121" com buracos entre os dígitos.
const heroValueStyle = {
  fontFamily: FONT,
  fontSize: 56,
  fontWeight: 600,
  letterSpacing: "-0.03em",
  lineHeight: 1,
  color: COLORS.ink,
};

const labelStyle = {
  fontFamily: FONT,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: COLORS.inkMuted,
  lineHeight: 1.35,
  // Espaço de duas linhas mesmo quando o rótulo cabe em uma: sem isso, num
  // layout estreito "PESSOAS" fica numa linha e "MÉDIA POR DIA ÚTIL" em duas,
  // e os números da fileira deixam de alinhar entre si.
  minHeight: 30,
};

const tileValueStyle = {
  fontFamily: FONT,
  fontSize: 28,
  fontWeight: 600,
  letterSpacing: "-0.02em",
  lineHeight: 1.1,
  color: COLORS.ink,
};

const tileHintStyle = {
  fontFamily: FONT,
  fontSize: 12,
  color: COLORS.inkSecondary,
  marginTop: 4,
};

/**
 * Variação contra o período anterior.
 *
 * A seta é o canal redundante: quem não distingue verde de vermelho lê ▲/▼.
 * Aqui "para cima" é bom por definição — mais registro é mais conformidade.
 */
function Trend({ pct, previous }) {
  if (pct === null || pct === undefined) {
    return (
      <span style={{ fontFamily: FONT, fontSize: 12.5, color: COLORS.inkMuted }}>
        sem base de comparação no período anterior
      </span>
    );
  }

  const up = pct >= 0;
  const color = up ? COLORS.good : COLORS.critical;

  return (
    <Tooltip title={`${previous} preenchimento(s) na janela anterior, do mesmo tamanho`}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FONT }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            fontSize: 12.5,
            fontWeight: 600,
            color,
            backgroundColor: up ? "rgba(12, 163, 12, 0.10)" : "rgba(208, 59, 59, 0.10)",
            padding: "3px 8px",
            borderRadius: 999,
          }}
        >
          {up ? "▲" : "▼"} {decimal(Math.abs(pct))}%
        </span>
        <span style={{ fontSize: 12.5, color: COLORS.inkMuted }}>vs. período anterior</span>
      </span>
    </Tooltip>
  );
}

/**
 * Sparkline do card herói: a forma do período, sem eixo nem grade.
 *
 * SVG na mão em vez de recharts — são 30 pontos sem interação, e um container
 * responsivo do recharts aqui é peso morto. O `preserveAspectRatio="none"`
 * deixa o desenho esticar na largura do card.
 */
function Sparkline({ points }) {
  if (points.length < 2) {
    return null;
  }

  const width = 100;
  const height = 28;
  const max = Math.max(...points, 1);
  const step = width / (points.length - 1);
  const coords = points.map((value, index) => [
    index * step,
    height - (value / max) * (height - 2) - 1,
  ]);

  const line = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x} ${y}`).join(" ");
  const area = `${line} L${width} ${height} L0 ${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      // Ocupa a altura que sobrar no card: quando o card estica (porque a
      // coluna ao lado é mais alta), o vazio vira gráfico maior em vez de
      // buraco.
      style={{ display: "block", width: "100%", height: "100%", minHeight: 34 }}
    >
      {/* Área a ~10% de opacidade: um banho, nunca um bloco saturado. */}
      <path d={area} fill={SERIES.fills} fillOpacity="0.12" />
      <path
        d={line}
        fill="none"
        stroke={SERIES.fills}
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function Tile({ label, value, hint, tip }) {
  return (
    <Tooltip title={tip}>
      <div
        style={{
          ...cardStyle,
          padding: "16px 18px",
          boxSizing: "border-box",
        }}
      >
        <div style={labelStyle}>{label}</div>
        <div style={{ ...tileValueStyle, marginTop: 10 }}>{value}</div>
        <div style={tileHintStyle}>{hint}</div>
      </div>
    </Tooltip>
  );
}

/**
 * Barra de cobertura.
 *
 * Um "50%" solto não diz se são 3 de 6 ou 300 de 600, e duas porcentagens lado
 * a lado não se comparam de cabeça. A barra resolve as duas coisas: o trilho é
 * um passo claro da própria cor, então o estado se lê ao longo de toda a barra.
 */
function Meter({ label, pct, done, total, hint }) {
  return (
    <div style={{ flex: "1 1 240px", minWidth: 220 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontFamily: FONT, fontSize: 13, color: COLORS.ink, fontWeight: 500 }}>
          {label}
        </span>
        <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: COLORS.ink }}>
          {decimal(pct)}%
        </span>
      </div>
      <div
        style={{
          height: 8,
          borderRadius: 999,
          backgroundColor: "rgba(31, 138, 91, 0.14)",
          marginTop: 8,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.min(100, Math.max(0, pct))}%`,
            backgroundColor: SERIES.fills,
            borderRadius: 999,
            transition: "width 240ms ease",
          }}
        />
      </div>
      <div style={{ ...tileHintStyle, marginTop: 6 }}>
        {done} de {total} · {hint}
      </div>
    </div>
  );
}

export default function StatTiles({ kpis, periodLabel, series = [] }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
      {/* Herói: um por tela. É o número que a pessoa veio ver. */}
      <div
        style={{
          ...cardStyle,
          flex: "1 1 300px",
          minWidth: 280,
          padding: "20px 22px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div style={labelStyle}>Preenchimentos</div>
          <div style={{ ...heroValueStyle, marginTop: 12 }}>
            {kpis.total.toLocaleString("pt-BR")}
          </div>
          <div style={{ fontFamily: FONT, fontSize: 12.5, color: COLORS.inkSecondary, marginTop: 6 }}>
            {periodLabel}
          </div>
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 34,
            display: "flex",
            alignItems: "flex-end",
            margin: "16px 0 12px",
          }}
        >
          <Sparkline points={series.map((point) => point.total)} />
        </div>
        <Trend pct={kpis.trend_pct} previous={kpis.previous_total} />
      </div>

      <div style={{ flex: "3 1 520px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 14,
          }}
        >
        <Tile
          label="Média por dia útil"
          value={decimal(kpis.avg_per_active_day)}
          hint={`${kpis.avg_per_calendar_day.toString().replace(".", ",")} por dia corrido`}
          tip="Total dividido pelos dias em que houve ao menos um preenchimento. O segundo número usa todos os dias do período."
        />
        <Tile
          label="Dias com registro"
          value={`${kpis.active_days}/${kpis.calendar_days}`}
          hint={`${Math.round((kpis.active_days / Math.max(1, kpis.calendar_days)) * 100)}% dos dias do período`}
          tip="Dias do período em que houve ao menos um preenchimento."
        />
        <Tile
          label="Pessoas"
          value={kpis.distinct_users}
          hint="preencheram no período"
          tip="Quantas pessoas diferentes registraram algum preenchimento."
        />
        <Tile
          label="Formulários usados"
          value={kpis.distinct_forms}
          hint="receberam registro"
          tip="Quantos formulários diferentes tiveram ao menos um preenchimento."
        />

        </div>

        <Card
          title="Cobertura de assinatura"
          hint="Quanto do que foi preenchido já passou pelas etapas seguintes"
          flex="1 1 100%"
          minWidth={280}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 26 }}>
            <Meter
              label="Monitorados"
              pct={kpis.supervised_pct}
              done={kpis.supervised}
              total={kpis.total}
              hint={
                kpis.avg_hours_to_supervise === null
                  ? "sem tempo médio ainda"
                  : `${decimal(kpis.avg_hours_to_supervise)}h em média até assinar`
              }
            />
            <Meter
              label="Verificados"
              pct={kpis.checked_pct}
              done={kpis.checked}
              total={kpis.total}
              hint={
                kpis.avg_hours_to_check === null
                  ? "sem tempo médio ainda"
                  : `${decimal(kpis.avg_hours_to_check)}h em média até assinar`
              }
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

/**
 * Indicadores secundários, numa tira compacta.
 *
 * Anexos, edições e compartilhamentos importam, mas não competem com o número
 * principal — por isso vêm menores e depois dos gráficos.
 */
export function SecondaryStats({ kpis }) {
  const items = [
    {
      label: "Com anexo",
      value: kpis.with_attachments,
      tip: "Preenchimentos com pelo menos um arquivo anexado ativo.",
    },
    {
      label: "Editados",
      value: kpis.edited,
      tip: "Preenchimentos alterados depois de salvos (têm log de versão).",
    },
    {
      label: "Solicitações de edição",
      value: kpis.change_requests,
      tip: "Preenchimentos com pedido de alteração registrado.",
    },
    {
      label: "Compartilhados",
      value: `${kpis.shared_completed}/${kpis.shared}`,
      tip: "Preenchimentos por link compartilhado que foram concluídos, sobre o total enviado.",
    },
  ];

  return (
    <div
      style={{
        ...cardStyle,
        display: "flex",
        flexWrap: "wrap",
        padding: 0,
        overflow: "hidden",
        borderRadius: RADIUS,
      }}
    >
      {items.map((item, index) => (
        <Tooltip key={item.label} title={item.tip}>
          <div
            style={{
              flex: "1 1 160px",
              minWidth: 150,
              padding: "14px 18px",
              boxSizing: "border-box",
              borderLeft: index === 0 ? "none" : `1px solid ${COLORS.border}`,
            }}
          >
            <div style={labelStyle}>{item.label}</div>
            <div
              style={{
                fontFamily: FONT,
                fontSize: 20,
                fontWeight: 600,
                color: COLORS.ink,
                marginTop: 6,
              }}
            >
              {compactNumber(item.value)}
            </div>
          </div>
        </Tooltip>
      ))}
    </div>
  );
}
